// src/store/userSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiFetch } from "../../utils";

// Async thunk to fetch user profile
export const fetchUser = createAsyncThunk("user/getProfile", async () => {
    const res = await apiFetch(`api/haki/profile/`, { method: "GET" });
    return await res.json();
});

// Async thunk to update user profile
export const updateUser = createAsyncThunk(
    "user/updateUser",
    async ({ userId, userData }, { dispatch }) => {
        await apiFetch(`api/haki/profile`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(userData),
        });

        // Fetch fresh profile after update
        dispatch(fetchUser(userId));
        return userData;
    }
);

// Async thunk to connect a Discord account
export const connectDiscord = createAsyncThunk(
    "user/connectDiscord",
    async ({ userId, code }, { dispatch }) => {
        if (!userId || !code) throw new Error("Missing userId or code");

        const res = await apiFetch("api/haki/socials/discord/connect", {
            method: "POST",
            body: JSON.stringify({ userId, code }),
            headers: { "Content-Type": "application/json" },
        });

        if (!res.ok) {
            const errText = await res.text();
            throw new Error(`Failed to link Discord: ${errText}`);
        }

        // Refresh user profile in Redux
        await dispatch(fetchUser());

        return await res.json(); // Optional: the linked Discord profile
    }
);


// Async thunk to disconnect Discord account
export const disconnectDiscord = createAsyncThunk(
    "user/disconnectDiscord",
    async (_, { dispatch }) => {
        const res = await apiFetch("api/haki/socials/discord/disconnect", { method: "POST" });
        if (!res.ok) throw new Error("Failed to unlink Discord");

        // Refresh user data
        dispatch(fetchUser());
        return {};
    }
);

// Get Discord OAuth URL
export const getDiscordOAuthUrl = createAsyncThunk(
    "user/getDiscordOAuthUrl",
    async () => {
        const res = await apiFetch("api/haki/socials/discord/oauth-url");
        const data = await res.json();
        return data.url;
    }
);

export const fetchDashboardStats = createAsyncThunk(
    "user/fetchDashboardStats",
    async () => {
        const res = await apiFetch("api/haki/dashboard");
        if (!res.ok) throw new Error("Failed to fetch dashboard stats");
        const data = await res.json();
        return data.stats; // { totalProjects, totalDeployments, activeServers }
    }
);

const userSlice = createSlice({
    name: "user",
    initialState: {
        data: null,
        loading: false,
        error: null,
        stats: {
            totalProjects: 0,
            totalDeployments: 0,
            activeServers: 0,
        },
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Fetch user
            .addCase(fetchUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUser.fulfilled, (state, action) => {
                state.data = action.payload;
                state.loading = false;
            })
            .addCase(fetchUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            // Update user
            .addCase(updateUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateUser.fulfilled, (state, action) => {
                state.data = action.payload;
                state.loading = false;
            })
            .addCase(updateUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            // Connect Discord
            .addCase(connectDiscord.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(connectDiscord.fulfilled, (state, action) => {
                state.loading = false;
                // Optionally store Discord profile in state
                if (state.data) state.data.socials = { ...state.data.socials, discord: true };
            })
            .addCase(connectDiscord.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            // Disconnect Discord
            .addCase(disconnectDiscord.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(disconnectDiscord.fulfilled, (state, action) => {
                state.loading = false;
                if (state.data) state.data.socials = { ...state.data.socials, discord: false };
            })
            .addCase(disconnectDiscord.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(fetchDashboardStats.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchDashboardStats.fulfilled, (state, action) => {
                state.stats = action.payload;
                state.loading = false;
            })
            .addCase(fetchDashboardStats.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            });
    },
});

export default userSlice.reducer;
